import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { TaskDetailProps } from "../types";
import { PaperCheckbox } from "./TaskRow";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Things-style card that expands in place, lifting slightly off the list. Inline, its checkbox and
 * title stay on the rows' columns (the card's -4px outset plus 14px inset = the rows' 10px).
 */
export function PaperTaskDetail({ task, mode, onClose, onToggleComplete, parts }: TaskDetailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const closed = task.status === "done" || task.status === "cancelled";

  useEffect(() => {
    if (mode !== "inline") return;
    ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [mode, task.id]);

  const props = parts.properties;
  return (
    <div
      ref={ref}
      data-task-detail={task.id}
      role="group"
      aria-label={`Task ${task.readableId}`}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      className={cn(
        "relative flex flex-col bg-card text-card-foreground",
        mode === "inline" && "-mx-1 my-2 animate-expand rounded-xl border border-border/70 shadow-card",
        mode !== "inline" && "h-full",
      )}
    >
      <div className="flex items-start gap-3 pb-1 pl-3.5 pr-4 pt-3.5">
        <span className="flex h-[26px] items-center">
          <PaperCheckbox
            checked={closed}
            cancelled={task.status === "cancelled"}
            onToggle={onToggleComplete}
            label={closed ? "Reopen task" : "Complete task"}
          />
        </span>
        <div className="min-w-0 flex-1">{parts.title}</div>
        <Button size="icon" variant="quiet" aria-label="Close detail" onClick={onClose} className="-mr-1.5 mt-0.5">
          <X />
        </Button>
      </div>
      <div className="pl-11 pr-4">
        {parts.syncNotice}
        {parts.notes}
        <div className="mt-1">{parts.checklist}</div>
      </div>
      <div data-detail-chips className="mt-2 flex flex-wrap items-center gap-0.5 border-t border-border/60 px-3 py-2">
        {props.when}
        {props.deadline}
        {props.repeat}
        {props.priority}
        {props.status}
        {props.labels}
        {props.location}
        {props.reminder}
        {props.snooze}
        <div className="ml-auto flex items-center gap-1">{parts.actions}</div>
      </div>
      <div className="flex items-center gap-3 px-4 pb-2.5 text-[12px] text-subtle">{parts.meta}</div>
    </div>
  );
}
